import { Component, OnInit, input, inject, ChangeDetectionStrategy } from "@angular/core";
import {
  ChangeDirectoryCommand,
  FileNode,
  FileType,
  LibraryDirectory,
  LibraryDocument,
  LibraryType,
} from "../library.model";
import { SelectionModel } from "@angular/cdk/collections";
import { MatDialog } from "@angular/material/dialog";
import { DatabaseService } from "../../shared/database.service";
import { LibraryService } from "../library.service";
import { MasterDataService } from "../../master-data/master-data-service";
import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { finalize, first } from "rxjs/operators";
import { LibraryDocumentEditComponent } from "../library-document-edit/library-document-edit.component";
import {
  ConfDialogType,
  ConfirmDialogModel,
} from "../../shared/confirm-dialog/confim-dialog.model";
import {
  CONFIRM_DIALOG_CONFIRM_ACTION,
  CONFIRM_DIALOG_LIBRARY_DIR_DELETE,
  CONFIRM_DIALOG_LIBRARY_DOC_DELETE,
} from "../../../locale/consts.en";
import { ConfirmDialogComponent } from "../../shared/confirm-dialog/confirm-dialog.component";
import { LibraryDirectoryEditComponent } from "../library-directory-edit/library-directory-edit.component";
import {DocumentViewerComponent} from '../../shared/document-viewer/document-viewer.component';

interface TreeNode extends FileNode {
  level: number;
  isVisible: boolean;
  canView: boolean;
}

@Component({
  selector: "app-library-documents-tree",
  templateUrl: "./library-documents-tree.component.html",
  styleUrls: ["./library-documents-tree.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LibraryDocumentsTreeComponent implements OnInit {
  private dialog = inject(MatDialog);
  private databaseService = inject(DatabaseService);
  private libraryService = inject(LibraryService);
  private masterDataService = inject(MasterDataService);

  readonly libraryType = input<LibraryType>(undefined);

  public FileT = FileType;

  // Manual tree management
  flatTreeData: TreeNode[] = [];
  originalTreeData: FileNode[] = [];

  // expansion model tracks expansion state
  expansionModel = new SelectionModel<string>(true);
  dragging = false;
  expandTimeout: any;
  expandDelay = 1000;

  libraryLoaded: boolean | null = null;
  LibraryT = LibraryType;

  constructor() {}

  private static compare(
    a: number | string | Date,
    b: number | string | Date,
    isAsc: boolean
  ): number {
    let result = 0;

    if (typeof a === 'string' && typeof b === 'string') {
      result = a.localeCompare(b, 'pl', {
        sensitivity: 'base',
        numeric: true
      });
    } else {
      if (a < b) result = -1;
      else if (a > b) result = 1;
      else result = 0;
    }

    return isAsc ? result : -result;
  }

  // Utility function to get node level
  private levelAccessor = (node: FileNode, level: number = 0): number => level;

  // Utility function to get children
  private childrenAccessor = (node: FileNode): FileNode[] => node.children || [];

  // Check if node has children
  hasChildren(node: FileNode): boolean {
    const children = this.childrenAccessor(node);
    return children && children.length > 0;
  }

  // Check if node is expanded
  isExpanded(node: FileNode): boolean {
    return this.expansionModel.isSelected(node.uuid);
  }

  // Toggle node expansion
  toggleNode(node: FileNode): void {
    this.expansionModel.toggle(node.uuid);
    this.rebuildFlatTree();
  }

  // Flatten tree structure for display
  private flattenTree(nodes: FileNode[], level: number = 0, parentExpanded: boolean = true): TreeNode[] {
    const result: TreeNode[] = [];

    if (!nodes) return result;

    for (const node of nodes) {
      const treeNode: TreeNode = {
        ...node,
        level,
        isVisible: parentExpanded,
        canView: node.type === FileType.FILE ? this.canViewFileType(node.name) : false
      };

      result.push(treeNode);

      if (this.hasChildren(node) && this.isExpanded(node) && parentExpanded) {
        const children = this.flattenTree(
          this.childrenAccessor(node),
          level + 1,
          true
        );
        result.push(...children);
      }
    }

    return result;
  }

  private canViewFileType(fileName: string): boolean {
    return true; // not availabe file extension from API - allow see all documents
    // if (!fileName) return false;
    // const extension = fileName.toLowerCase().split('.').pop();
    // return ['pdf', 'jpg', 'jpeg', 'png'].includes(extension || '');
  }

  // Rebuild the flat tree for display
  private rebuildFlatTree(): void {
    this.flatTreeData = this.flattenTree(this.originalTreeData);
  }

  // Get visible nodes (for drag and drop)
  getVisibleNodes(): TreeNode[] {
    return this.flatTreeData.filter(node => node.isVisible);
  }

  ngOnInit(): void {
    this.libraryService.readDirectories(this.libraryType());
    this.loadFileHierarchy();
  }

  /**
   * Handle the drop - updated for manual tree management
   */
  drop(event: CdkDragDrop<string[]>) {
    // ignore drops outside the tree
    if (!event.isPointerOverContainer) {
      return;
    }

    // get visible nodes for drop calculation
    const visibleNodes = this.getVisibleNodes();

    // deep clone the data source so we can mutate it
    const changedData = JSON.parse(JSON.stringify(this.originalTreeData));

    // recursive find function to find siblings of node
    const findNodeSiblings = (
      arr: Array<FileNode>,
      uuid: string
    ): Array<FileNode> => {
      let result;
      let subResult;
      arr.forEach((item) => {
        if (item.uuid === uuid) {
          result = arr;
        } else if (item.children) {
          subResult = findNodeSiblings(item.children, uuid);
          if (subResult) {
            result = subResult;
          }
        }
      });
      return result;
    };

    // recursive find function to find parent Uuid of node
    const findNodeParent = (
      arr: Array<FileNode>,
      uuid: string,
      parUuid: string | null
    ): string => {
      let result = null;
      let subResult;

      arr.forEach((item) => {
        if (item.uuid === uuid) {
          result = parUuid;
        } else if (item.children) {
          subResult = findNodeParent(item.children, uuid, item.uuid);
          if (subResult) {
            result = subResult;
          }
        }
      });
      return result;
    };

    // determine where to insert the node
    const nodeAtDest = visibleNodes[event.currentIndex];
    const newSiblings = findNodeSiblings(changedData, nodeAtDest.uuid);
    if (!newSiblings) {
      return;
    }
    const insertIndex = newSiblings.findIndex(
      (s) => s.uuid === nodeAtDest.uuid
    );

    // remove the node from its old place
    const node = event.item.data;
    const siblings = findNodeSiblings(changedData, node.uuid);
    const siblingIndex = siblings.findIndex((n) => n.uuid === node.uuid);
    const nodeToInsert: FileNode = siblings.splice(siblingIndex, 1)[0];
    if (nodeAtDest.uuid === nodeToInsert.uuid) {
      return;
    }

    newSiblings.splice(insertIndex, 0, nodeToInsert);

    // rebuild tree with mutated data
    this.originalTreeData = changedData;
    this.rebuildFlatTree();

    const parentUuid = findNodeParent(changedData, node.uuid, null);

    const cmd: ChangeDirectoryCommand = {
      uuid: node.uuid,
      directoryUuid: parentUuid,
    };
    console.log("CMD", cmd);

    if (node.type === FileType.DIRECTORY) {
      this.databaseService
        .changeDirLocationInLibrary(cmd)
        .pipe(finalize(() => this.loadFileHierarchy()))
        .subscribe({
          next: (response) => {
            if (response.status !== 200 && response.status !== 201) {
              console.error("Directory Update Error by API");
            }
          },
          error: () => this.masterDataService.savingErrorMessage(),
        });
    } else {
      this.databaseService
        .changeFileLocationInLibrary(cmd)
        .pipe(finalize(() => this.loadFileHierarchy()))
        .subscribe({
          next: (response) => {
            if (response.status !== 200 && response.status !== 201) {
              console.error("Directory Update Error by API");
            }
          },
          error: () => this.masterDataService.savingErrorMessage(),
        });
    }
  }

  /**
   * Drag and drop event handlers
   */
  dragStart() {
    this.dragging = true;
  }

  dragEnd() {
    this.dragging = false;
  }

  dragHover(node: TreeNode) {
    if (this.dragging && this.hasChildren(node)) {
      clearTimeout(this.expandTimeout);
      this.expandTimeout = setTimeout(() => {
        if (!this.isExpanded(node)) {
          this.toggleNode(node);
        }
      }, this.expandDelay);
    }
  }

  dragHoverEnd() {
    if (this.dragging) {
      clearTimeout(this.expandTimeout);
    }
  }

  loadFileHierarchy() {
    const sortTree = (tree: FileNode[]): FileNode[] =>
      [...tree]
        .map(node => ({
          ...node,
          children:
            node.type === FileType.DIRECTORY && node.children?.length
              ? sortTree(node.children)
              : node.children ?? []
        }))
        .sort((a, b) => {
          if (a.type !== b.type) {
            return a.type === FileType.DIRECTORY ? -1 : 1;
          }

          return LibraryDocumentsTreeComponent.compare(a.name, b.name, true);
        });

    this.databaseService
      .loadFileHierarchy(this.libraryType())
      .pipe(first())
      .subscribe((docs) => {
        this.originalTreeData = sortTree(docs);
        this.rebuildFlatTree();
      });
  }

  onAddDocument(libraryType: LibraryType) {
    const libraryDocument = new LibraryDocument(libraryType);
    this.openCreateEditDocumentDialog(libraryDocument);
  }

  updateDocument(uuid: string, libraryType: LibraryType) {
    this.databaseService.loadDocument(uuid).subscribe({
      next: (doc) => {
        doc.libraryType = libraryType;
        this.openCreateEditDocumentDialog(doc);
      },
      error: () => {
        console.log("Error - cannot load document from server");
      },
    });
  }

  private openCreateEditDocumentDialog(libraryDocument: LibraryDocument) {
    const dialogRef = this.dialog.open(LibraryDocumentEditComponent, {
      width: "20vw",
      data: libraryDocument,
    });
    dialogRef.afterClosed().subscribe(async (data: boolean) => {
      if (data === true) {
        this.loadFileHierarchy();
      }
    });
  }

  deleteDocument(docUuid: string) {
    const dialogData = new ConfirmDialogModel(
      CONFIRM_DIALOG_CONFIRM_ACTION,
      CONFIRM_DIALOG_LIBRARY_DOC_DELETE,
      ConfDialogType.CONFIRM
    );

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.databaseService
          .deleteDocumentInLibrary(docUuid)
          .pipe(
            finalize(() => {
              this.loadFileHierarchy();
            })
          )
          .subscribe({
            next: (response) => {
              this.masterDataService.handleResponse(response);
            },
            error: () => this.masterDataService.savingErrorMessage(),
          });
      }
    });
  }

  viewDocument(docUuid: string) {
    this.databaseService.loadDocument(docUuid).subscribe({
      next: (doc) => {
        this.openDocumentViewDialog(doc);
      },
      error: () => {
        console.log("Error - cannot load document from server");
        this.masterDataService.savingErrorMessage();
      },
    });
  }

  private openDocumentViewDialog(document: LibraryDocument) {
    const dialogRef = this.dialog.open(DocumentViewerComponent, {
      width: "90vw",
      height: "90vh",
      maxWidth: "1200px",
      maxHeight: "800px",
      data: document,
    });
  }

  onAddDirectory(libraryType: LibraryType) {
    const directory = new LibraryDirectory(libraryType);
    const dialogRef = this.dialog.open(LibraryDirectoryEditComponent, {
      width: "20vw",
      data: directory,
    });
    dialogRef.afterClosed().subscribe(async (data: boolean) => {
      if (data === true) {
        this.libraryService.readDirectories(libraryType);
        this.loadFileHierarchy();
      }
    });
  }

  updateDirectory(uuid: string) {
    this.databaseService.loadDirectory(uuid).subscribe({
      next: (doc) => {
        console.log("DIR", doc);
        this.openCreateEditDirectoryDialog(doc);
      },
      error: () => {
        console.log("Error - cannot load document from server");
      },
    });
  }

  private openCreateEditDirectoryDialog(libraryDir: LibraryDirectory) {
    const dialogRef = this.dialog.open(LibraryDirectoryEditComponent, {
      width: "20vw",
      data: libraryDir,
    });
    dialogRef.afterClosed().subscribe(async (data: boolean) => {
      if (data === true) {
        this.libraryService.readDirectories(libraryDir.type);
        this.loadFileHierarchy();
      }
    });
  }

  deleteDirectory(dirUuid: string, type: LibraryType) {
    const dialogData = new ConfirmDialogModel(
      CONFIRM_DIALOG_CONFIRM_ACTION,
      CONFIRM_DIALOG_LIBRARY_DIR_DELETE,
      ConfDialogType.CONFIRM
    );

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.databaseService
          .deleteDirectoryInLibrary(dirUuid)
          .pipe(
            finalize(() => {
              this.libraryService.readDirectories(type);
              this.loadFileHierarchy();
            })
          )
          .subscribe({
            next: (response) => {
              this.masterDataService.handleResponse(response);
            },
            error: () => this.masterDataService.savingErrorMessage(),
          });
      }
    });
  }
}
